/**
 * Примитивы содержимого сайдбара: блок, строка «название — значение»,
 * текстовое действие.
 *
 * Жили в стенде, пока сайдбар был только стендом. С переездом туда игровой
 * панели они стали общими: две разные панели в одной колонке обязаны выглядеть
 * одинаково, иначе граница между ними читается как граница между приложениями.
 */

import type { ReactNode } from 'react'
import {
  chooseEngine,
  choiceValue,
  DEFAULT_ENGINE,
  ENGINE_OPTIONS,
  useEngineChoice,
} from './engines/index.js'

/** Действие текстом, а не кнопкой: в плотной колонке кнопка была бы громкой. */
export function Action({
  onClick,
  children,
  tone = 'plain',
  disabled,
}: {
  onClick: () => void
  children: ReactNode
  tone?: 'plain' | 'good' | 'bad' | 'warn'
  disabled?: boolean
}) {
  const tones = {
    plain: 'text-steel-l enabled:hover:text-text',
    good: 'text-turq enabled:hover:text-text',
    bad: 'text-danger-l enabled:hover:text-text',
    warn: 'text-champ enabled:hover:text-text',
  } as const
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`cursor-pointer font-mono text-[11px] lowercase underline decoration-transparent underline-offset-4 enabled:hover:decoration-current disabled:cursor-default disabled:opacity-35 ${tones[tone]}`}
    >
      {children}
    </button>
  )
}

export function Block({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="border-b border-steel-d/25 px-4 py-3">
      {title && (
        <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-path">{title}</h3>
      )}
      {children}
    </section>
  )
}

/**
 * Сетка строки сайдбара: название, значение, действие. Действия стоят в правой колонке одной
 * ширины, у самого края, как `clear` в журнале: значения над ней выстраиваются по одной линии.
 */
export const ROW_GRID = 'grid grid-cols-[minmax(0,1fr)_auto_minmax(4.5rem,auto)] items-baseline gap-x-3'

/**
 * Строка «название — значение — действие» по той же сетке, что комнаты и игроки: название светлым слева
 * (светодиод состояния перед ним, а не между значением и действием), значение моноширинным справа,
 * действие в правой колонке.
 */
export function Row({
  label,
  value,
  action,
  led,
}: {
  /** Название строки; у строки состояния его заменяет само состояние. */
  label: ReactNode
  value?: ReactNode
  action?: ReactNode
  /** Светодиод стоит перед названием, как маркер строки. */
  led?: ReactNode
}) {
  return (
    <div className={`${ROW_GRID} py-1 text-xs`}>
      {/* Обрезаем только текст: светодиод стоит снаружи, иначе overflow срезал бы свечение. */}
      <span className="flex min-w-0 items-center gap-2">
        {led}
        <span className="min-w-0 truncate text-text">{label}</span>
      </span>
      <span className="shrink-0 whitespace-nowrap text-right font-mono text-[11px] text-steel-l">
        {value}
      </span>
      {/* Действия прижаты вправо; колонка не уже 4.5rem и растёт, когда их несколько, чтобы не лезть на значение. */}
      <span className="flex justify-end gap-3 whitespace-nowrap">{action}</span>
    </div>
  )
}

/** Выбор реализации сцены: html или pixi с отрисовщиком. Смена — на лету, без перезагрузки. */
export function EngineSelect({ className = '' }: { className?: string }) {
  const value = choiceValue(useEngineChoice())
  return (
    <select
      value={ENGINE_OPTIONS.some((o) => o.value === value) ? value : DEFAULT_ENGINE}
      onChange={(e) => chooseEngine(e.target.value)}
      aria-label="Scene engine"
      className={`cursor-pointer rounded border border-steel-d/40 bg-deep px-1 py-0.5 font-mono text-[11px] text-steel-l ${className}`}
    >
      {ENGINE_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  )
}
